import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { UserProfile } from '../../types';

interface LocationStepProps {
  formData: UserProfile;
  errors: Record<string, string>;
  onFieldChange: (field: string, value: any) => void;
  numberInputRef: React.RefObject<HTMLInputElement>;
}

export default function LocationStep({
  formData,
  errors,
  onFieldChange,
  numberInputRef
}: LocationStepProps) {
  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">CEP</label>
        <input
          type="text"
          value={formData.zipCode || ''}
          onChange={(e) => {
            const raw = e.target.value.replace(/\D/g, '').slice(0, 8);
            let formatted = raw;
            if (raw.length > 5) {
              formatted = `${raw.slice(0, 5)}-${raw.slice(5, 8)}`;
            }
            onFieldChange('zipCode', formatted);
          }}
          className={`w-full bg-white border ${errors.zipCode ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
          placeholder="00000-000"
          maxLength={9}
        />
        {errors.zipCode && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.zipCode}</p>}
      </div>

      <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-4 mb-2">
        <label className="flex items-center gap-3 cursor-pointer group">
          <div className="relative flex items-center">
            <input
              type="checkbox"
              checked={formData.showAddress || false}
              onChange={(e) => onFieldChange('showAddress', e.target.checked)}
              className="peer h-5 w-5 appearance-none rounded-md border-2 border-zinc-200 checked:bg-blue-600 checked:border-blue-600 transition-all"
            />
            <CheckCircle2 className="absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100 left-1 transition-opacity" />
          </div>
          <span className="text-xs text-zinc-600 font-medium group-hover:text-zinc-900 transition-colors">
            Exibir endereço nos orçamentos?
          </span>
        </label>
        <p className="text-[10px] text-zinc-400 mt-2 ml-8 leading-tight">
          Se desmarcado, seus dados de localização ficarão ocultos no PDF.
        </p>
      </div>

      <div className="flex gap-4">
        <div className="space-y-1 flex-3">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Endereço</label>
          <input
            type="text"
            value={formData.businessAddress || ''}
            onChange={(e) => onFieldChange('businessAddress', e.target.value)}
            className={`w-full bg-white border ${errors.businessAddress ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
            placeholder="Nome da rua, bairro"
          />
          {errors.businessAddress && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.businessAddress}</p>}
        </div>
        <div className="space-y-1 flex-1">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Número</label>
          <input
            ref={numberInputRef}
            type="text"
            value={formData.businessNumber || ''}
            onChange={(e) => onFieldChange('businessNumber', e.target.value)}
            className="w-full bg-white border border-zinc-100 rounded-2xl py-4 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all"
            placeholder="Nº"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Cidade</label>
          <input
            type="text"
            value={formData.city || ''}
            onChange={(e) => onFieldChange('city', e.target.value)}
            className={`w-full bg-white border ${errors.city ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
          />
          {errors.city && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.city}</p>}
        </div>
        <div className="space-y-1">
          <label className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest ml-1">Estado</label>
          <input
            type="text"
            value={formData.state || ''}
            onChange={(e) => onFieldChange('state', e.target.value.toUpperCase())}
            className={`w-full bg-white border ${errors.state ? 'border-rose-500' : 'border-zinc-100'} rounded-2xl py-4 px-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all`}
            maxLength={2}
          />
          {errors.state && <p className="text-[10px] text-rose-500 font-bold ml-1">{errors.state}</p>}
        </div>
      </div>
    </div>
  );
}
